import type { Product } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { Basket } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import {
	createGtmItem,
	type CreateGtmItemContext,
	type GtmItem
} from "../types/composables/useGtmEventsTypes";
import type { CombinedItem } from "~/types/components/category";

// Dotdigital tag payload shapes — the subset we populate. Mirrors the official
// tag API: https://marketing.developer.dotdigital.com/docs/dotdigital-tag-api-methods
interface DdgIdentity {
	email?: string;
	mobileNumber?: string;
	firstName?: string;
	lastName?: string;
	fullName?: string;
}
interface DdgProduct {
	// The product object's key is `id`; only cartProduct uses `productId`.
	id: string;
	sku: string;
	name: string;
	url: string;
	imageUrl: string;
	imageThumbnailUrl: string;
	price: number;
	currency: string;
	brand?: string;
	categories?: string[];
}
// [NF-460] `price` and `url`, as the cartProduct table in the docs requires.
// Their checkout example says `unitPrice`, but the table and the cartUpdate
// and purchaseComplete examples all use `price`.
interface DdgCartProduct {
	productId: string;
	id: string;
	sku: string;
	name: string;
	price: number;
	quantity: number;
	totalPrice: number;
	imageUrl: string;
	url: string;
	brand?: string;
}
interface DdgCart {
	cartId: string;
	currency: string;
	subtotal: number;
	grandTotal: number;
	cartUrl: string;
	products: DdgCartProduct[];
	shipping?: number;
	taxAmount?: number;
}

type DdgMethod =
	| "configuration"
	| "identify"
	| "productBrowse"
	| "productList"
	| "cartUpdate"
	| "checkout"
	| "purchaseComplete";

type DdgWindow = Window & {
	ddg?: Partial<Record<DdgMethod, (payload: unknown) => void>>;
	Cookiebot?: { consent?: { marketing?: boolean } };
};

// Events fired before the (lazy, consent-gated) tag is ready are buffered and
// flushed once Dotdigital signals readiness via the `ddgTagLoaded` event.
// Module-scoped so the queue + listener are shared across composable calls.
const pendingEvents: Array<[DdgMethod, unknown]> = [];
let tagListenerBound = false;
// Cart id from the most recent cartUpdate, reused by purchaseComplete so the
// order ties back to the same cart even if the success-page basket arrives
// without its Id. Client-only (the composable is a no-op on the server).
let lastCartId = "";
// [NF-460] Language and currency last sent with `configuration`, which the
// tag needs before the other calls. Re-sent only when either changes, and
// to every new tag instance (see onTagLoaded).
let lastConfiguration = "";
let lastConfigurationPayload: unknown = null;

const dispatchToTag = (method: DdgMethod, payload: unknown): boolean => {
	const ddg = (window as DdgWindow).ddg;
	if (ddg && typeof ddg[method] === "function") {
		ddg[method]!(payload);
		return true;
	}
	return false;
};

// Runs on every `ddgTagLoaded`, not just the first. breadandboxers removes
// the tag when consent is withdrawn and loads a fresh one when it is given
// again, and that instance has not seen `configuration` or the queued events.
const onTagLoaded = () => {
	if (lastConfigurationPayload)
		dispatchToTag("configuration", lastConfigurationPayload);
	// A queued `configuration` is skipped: the latest one was just sent.
	while (pendingEvents.length) {
		const queued = pendingEvents.shift();
		if (queued && queued[0] !== "configuration")
			dispatchToTag(queued[0], queued[1]);
	}
};

const noopApi = () => ({
	canTrack: () => false,
	identify: () => {},
	productBrowse: () => {},
	productList: () => {},
	cartUpdate: () => {},
	checkout: () => {},
	purchaseComplete: () => {}
});

/**
 * Dotdigital web-behaviour events (consent-gated), the Dotdigital counterpart
 * to `useGtmEvents`. The Dotdigital tag (`window.ddg`) does NOT read the GTM
 * dataLayer, so every event must be fired explicitly via the tag API. Calls
 * made before the (lazy, consent-gated) tag is ready are buffered and flushed
 * on the tag's `ddgTagLoaded` event, so early events aren't lost.
 *
 * Scope: event logic lives in base; the tag itself is loaded by a per-template
 * plugin (today only breadandboxers). Enable per deployment via the
 * `NORCE_DOTDIGITAL_ENABLED` env var (→ runtimeConfig.public.dotdigital.enabled)
 * so this stays a no-op where Dotdigital isn't configured.
 */
export const useDotdigital = () => {
	const config = useRuntimeConfig();
	const enabled = Boolean(
		(config.public as { dotdigital?: { enabled?: boolean } }).dotdigital
			?.enabled
	);
	if (!enabled || !import.meta.client) return noopApi();

	const { categories, hasDecided } = useCookieConsent();
	const norceStore = useNorceStore();
	const currency = norceStore?.currentCurrency?.Code || "SEK";
	const itemContext: CreateGtmItemContext = {
		cdnBaseUrl: (config.public.norce as { cdn?: string } | undefined)?.cdn,
		origin: window.location.origin
	};

	// Marketing consent — CMP-agnostic. Base templates use the custom consent
	// store (`useCookieConsent`); breadandboxers uses Cookiebot. A function (not
	// a computed) so the non-reactive `window.Cookiebot` read is re-evaluated on
	// every call — a computed would cache the first read and miss later consent
	// changes (e.g. tracking stuck off if first read happens before consent).
	const canTrack = () => {
		if (hasDecided.value && categories.value?.marketing === true) return true;
		return (window as DdgWindow).Cookiebot?.consent?.marketing === true;
	};

	const queue = (method: DdgMethod, payload: unknown) => {
		if (dispatchToTag(method, payload)) return;
		// Tag not loaded yet — buffer and flush on `ddgTagLoaded`.
		pendingEvents.push([method, payload]);
		if (!tagListenerBound) {
			tagListenerBound = true;
			window.addEventListener("ddgTagLoaded", onTagLoaded);
		}
	};

	// The tag requires site language (two letters, upper case, as in the
	// docs' example) and currency, so they go first on every event path.
	const configure = () => {
		const language =
			document.documentElement.lang?.split("-")[0]?.toUpperCase() || "";
		const key = `${language}|${currency}`;
		if (key === lastConfiguration) return;
		lastConfiguration = key;
		lastConfigurationPayload = { site: { language, currency } };
		queue("configuration", lastConfigurationPayload);
	};

	const send = (method: DdgMethod, payload: unknown) => {
		if (!canTrack()) return;
		configure();
		queue(method, payload);
	};

	const round2 = (value: number): number => Math.round(value * 100) / 100;

	const toCartProduct = (gi: GtmItem): DdgCartProduct => ({
		productId: gi.item_id != null ? String(gi.item_id) : "",
		sku: gi.item_sku ?? "",
		name: gi.item_name ?? "",
		// The cartProduct table names the key `productId`, but every cart
		// example in the same docs uses `id`. Both are sent until a client's
		// account shows which one Dotdigital reads.
		id: gi.item_id != null ? String(gi.item_id) : "",
		price: gi.price,
		quantity: gi.quantity,
		totalPrice: round2(gi.price * gi.quantity),
		imageUrl: gi.image_url ?? "",
		url: gi.product_url ?? "",
		...(gi.item_brand ? { brand: gi.item_brand } : {})
	});

	const toProduct = (gi: GtmItem): DdgProduct => ({
		id: gi.item_id != null ? String(gi.item_id) : "",
		sku: gi.item_sku ?? "",
		name: gi.item_name ?? "",
		url: gi.product_url ?? "",
		imageUrl: gi.image_url ?? "",
		imageThumbnailUrl: gi.image_url ?? "",
		price: gi.price,
		currency,
		...(gi.item_brand ? { brand: gi.item_brand } : {}),
		...(gi.item_category != null
			? { categories: [String(gi.item_category)] }
			: {})
	});

	const buildCart = (basket: Basket): DdgCart | null => {
		if (!basket?.Items?.length || !basket.Summary) return null;
		const products = basket.Items.map((item) =>
			createGtmItem(item, undefined, 1, itemContext)
		)
			.filter((gi): gi is GtmItem => gi != null)
			.map(toCartProduct);
		if (!products.length) return null;

		const shipping = basket.Summary.Freigt?.AmountIncVat;
		const taxAmount = basket.Summary.Total?.Vat;
		// Prefer the basket summary, but fall back to the line totals so a
		// completed/partial basket (the success page can deliver a zeroed summary)
		// never sends empty amounts — Dotdigital rejects an empty Subtotal /
		// GrandTotal on purchaseComplete.
		const lineTotal = round2(
			products.reduce((sum, product) => sum + product.totalPrice, 0)
		);
		const subtotal = basket.Summary.Items?.Amount || lineTotal;
		const grandTotal =
			basket.Summary.Total?.AmountIncVat || round2(lineTotal + (shipping ?? 0));

		return {
			// Cart id = the basket id used by cartUpdate. The ordered basket keeps
			// its Id (placeOrder only adds OrderReference), so this ties the
			// purchase back to the same cart.
			cartId: String(basket.Id ?? ""),
			currency: basket.CurrencyCode || currency,
			subtotal,
			grandTotal,
			cartUrl: `${window.location.origin}/checkout`,
			products,
			shipping,
			taxAmount
		};
	};

	return {
		canTrack,

		// Fire whenever a user reveals their email/mobile (login, signup,
		// checkout email, newsletter, pop-up). Augments existing contacts.
		identify(details: DdgIdentity) {
			const email = details.email?.trim().toLowerCase();
			if (!email && !details.mobileNumber) return;
			send("identify", {
				...(email ? { email } : {}),
				...(details.mobileNumber ? { mobileNumber: details.mobileNumber } : {}),
				...(details.firstName ? { firstName: details.firstName } : {}),
				...(details.lastName ? { lastName: details.lastName } : {}),
				...(details.fullName ? { fullName: details.fullName } : {})
			});
		},

		productBrowse(product: Product) {
			const gi = createGtmItem(product, undefined, 1, itemContext);
			if (gi) send("productBrowse", toProduct(gi));
		},

		// [NF-460] Products shown on a listing. Fired from useGtmEvents'
		// productImpression, so every listing that sends `view_item_list` sends
		// this too, with the same products.
		productList(items: Partial<FilteredProductItem>[] | CombinedItem[]) {
			const products = items
				.map((item) =>
					"type" in item && "data" in item
						? item.type === "product"
							? (item.data as Partial<FilteredProductItem>)
							: null
						: (item as Partial<FilteredProductItem>)
				)
				.filter((item): item is Partial<FilteredProductItem> => item != null)
				.map((item) => createGtmItem(item, undefined, 1, itemContext))
				.filter((gi): gi is GtmItem => gi != null)
				.map(toProduct);
			if (products.length) send("productList", products);
		},

		cartUpdate(basket: Basket) {
			const cart = buildCart(basket);
			if (!cart) return;
			if (cart.cartId) lastCartId = cart.cartId;
			send("cartUpdate", cart);
		},

		// [NF-460] Marks the cart as being checked out (cart phase
		// ORDER_CHECKOUT). Same payload as cartUpdate.
		checkout(basket: Basket) {
			const cart = buildCart(basket);
			if (!cart) return;
			if (cart.cartId) lastCartId = cart.cartId;
			send("checkout", cart);
		},

		purchaseComplete(basket: Basket) {
			const cart = buildCart(basket);
			if (!cart || !basket.OrderReference) return;
			// Prefer the ordered basket's own Id; fall back to the id captured from
			// the last cartUpdate so the order still ties back to that cart.
			const cartId = cart.cartId || lastCartId;
			// Unlike cartUpdate and checkout, the cart is nested under `cart`. The
			// earlier flat payload is what BB-141 saw rejected with
			// "PurchaseComplete.Cart.CartId must not be empty".
			send("purchaseComplete", {
				orderId: String(basket.OrderReference),
				cart: { ...cart, cartId }
			});
			// Cart is now ordered — drop the captured id so it can't leak into a
			// later order's fallback. A new cart re-populates it via cartUpdate.
			lastCartId = "";
		}
	};
};
